import type {
  ApiDashboardElement,
  DataSourceCandidatesResponse,
  SelectionSchemaProjectSite,
  WidgetEntity,
} from '@highimpact-frontend/api-types';
import { isWidgetEntitiesResponse } from '@highimpact-frontend/api-types';
import { getWidgetEntityKey } from '@highimpact/source-selector';

// -- type definitions --

export interface EntityIdOption {
  readonly id: number;
  readonly name: string;
}

// -- helper functions --

/**
 * Converts a v2 widget entity row into a project-site selection path.
 */
export function widgetEntityToProjectSiteSelection(
  entity: WidgetEntity
): SelectionSchemaProjectSite {
  return {
    project_id: entity.project_id,
    site_id: entity.site_id,
    group_index: 0,
  };
}

/**
 * Reads the saved project-site selection from an entity-scoped widget element.
 */
export function getProjectSiteSelectionFromElement(
  element: ApiDashboardElement
): SelectionSchemaProjectSite | undefined {
  const [selection] = element.data_source as SelectionSchemaProjectSite[];
  if (
    selection?.project_id == null ||
    selection?.site_id == null
  ) {
    return undefined;
  }

  return selection;
}

/**
 * Builds the initial v2 entity selection from saved element JSON.
 */
export function buildWidgetEntitySelectionFromElement(
  element: ApiDashboardElement,
  catalog: DataSourceCandidatesResponse | null
): WidgetEntity[] {
  const savedSelection = getProjectSiteSelectionFromElement(element);

  if (!savedSelection || !catalog || !isWidgetEntitiesResponse(catalog)) {
    return [];
  }

  const savedKey = `${savedSelection.project_id}:${savedSelection.site_id}`;

  const matchedEntity = catalog.entities.find(
    (entity) => getWidgetEntityKey(entity) === savedKey
  );

  return matchedEntity ? [matchedEntity] : [];
}

/**
 * Builds unique project or site options from a v2 entities catalog response.
 */
export function getEntityIdOptionsFromCatalog(
  entities: readonly WidgetEntity[],
  entityType: 'project' | 'site'
): readonly EntityIdOption[] {
  const optionsById = new Map<number, string>();

  entities.forEach((entity) => {
    if (entityType === 'project') {
      if (!optionsById.has(entity.project_id)) {
        optionsById.set(entity.project_id, entity.project_name);
      }
      return;
    }

    if (!optionsById.has(entity.site_id)) {
      optionsById.set(entity.site_id, entity.site_name);
    }
  });

  return [...optionsById.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((left, right) => left.name.localeCompare(right.name));
}

/**
 * Resolves the configured entity id/name pair against the fetched catalog.
 */
export function getSelectedEntityIdOption(
  entities: readonly WidgetEntity[],
  entityType: 'project' | 'site',
  entityId: number | undefined
): EntityIdOption | undefined {
  if (entityId === undefined) {
    return undefined;
  }

  return getEntityIdOptionsFromCatalog(entities, entityType).find(
    (option) => option.id === entityId
  );
}

/**
 * Finds a v2 widget entity row matching a text-from-entity-property selection.
 */
export function findWidgetEntityByTypeAndId(
  entities: readonly WidgetEntity[],
  entityType: 'project' | 'site',
  entityId: number
): WidgetEntity | undefined {
  if (entityType === 'site') {
    return entities.find((entity) => entity.site_id === entityId);
  }

  return entities.find((entity) => entity.project_id === entityId);
}
